// DocNavTree — the reader's left-column doc tree (RFC-0010 §6, PRD-0001 §1.3.1).
//
// Renders the nav tree reconstructed from the flat, ordered `NavItem[]` (RFC-0006)
// via `buildNavTree`. Leaf docs (non-null `docPath`) are react-router links to
// /projects/:repo/docs/<docPath>; pure section nodes render as non-interactive
// group headings with their children nested beneath. The active doc is
// highlighted from `currentDocPath`.

import { useRef, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { FileText } from '@phosphor-icons/react';
import type { NavItem } from '@portal/shared-types';
import { buildNavTree, type NavTreeNode } from '@/lib/doc-navigator';

interface DocNavTreeProps {
  nav: NavItem[];
  repo: string;
  currentDocPath: string | undefined;
}

function docHref(repo: string, docPath: string): string {
  return `/projects/${encodeURIComponent(repo)}/docs/${docPath}`;
}

function TreeNodes({
  nodes,
  repo,
  currentDocPath,
  depth,
}: {
  nodes: NavTreeNode[];
  repo: string;
  currentDocPath: string | undefined;
  depth: number;
}) {
  return (
    <ul className={depth === 0 ? 'flex flex-col gap-0.5' : 'mt-0.5 flex flex-col gap-0.5'}>
      {nodes.map((node) => {
        const isLeaf = node.docPath !== null;
        const active = isLeaf && node.docPath === currentDocPath;
        const indent = { paddingLeft: `${0.75 + depth * 0.75}rem` };

        return (
          <li key={`${node.position}`}>
            {isLeaf ? (
              <Link
                to={docHref(repo, node.docPath as string)}
                aria-current={active ? 'page' : undefined}
                style={indent}
                className={[
                  'flex items-center gap-2 rounded-lg py-2 pr-3 text-[13px] transition-colors',
                  active
                    ? 'bg-overlay/10 font-semibold text-text'
                    : 'font-medium text-text/50 hover:bg-overlay/5 hover:text-text/85',
                ].join(' ')}
              >
                <FileText size={14} weight={active ? 'fill' : 'regular'} className="shrink-0" />
                <span className="truncate">{node.title}</span>
              </Link>
            ) : (
              <p
                style={indent}
                className="py-2 pr-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted"
              >
                {node.title}
              </p>
            )}
            {node.children.length > 0 && (
              <TreeNodes
                nodes={node.children}
                repo={repo}
                currentDocPath={currentDocPath}
                depth={depth + 1}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function DocNavTree({ nav, repo, currentDocPath }: DocNavTreeProps) {
  const navRef = useRef<HTMLElement>(null);
  const tree = buildNavTree(nav);
  if (tree.length === 0) return null;

  // Arrow-key tree navigation (RFC-0010 §11 / PD-0008 G22): ArrowUp/ArrowDown
  // move focus between the tree's leaf links; Home/End jump to the first/last.
  // Tab/Shift+Tab and Enter (native link activation) keep working unchanged.
  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    const key = e.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') {
      return;
    }
    const root = navRef.current;
    if (!root) return;
    const links = Array.from(root.querySelectorAll<HTMLAnchorElement>('a[href]'));
    if (links.length === 0) return;
    e.preventDefault();

    const active = document.activeElement;
    const currentIndex = links.findIndex((l) => l === active);
    let nextIndex: number;
    if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = links.length - 1;
    else if (key === 'ArrowDown') nextIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, links.length - 1);
    else nextIndex = currentIndex < 0 ? links.length - 1 : Math.max(currentIndex - 1, 0);

    links[nextIndex]?.focus();
  }

  return (
    <nav aria-label="Documents" ref={navRef} onKeyDown={onKeyDown}>
      <TreeNodes nodes={tree} repo={repo} currentDocPath={currentDocPath} depth={0} />
    </nav>
  );
}
